import { useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useLocation, Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { navSections } from "./navItems";
import { tokens } from "../theme/tokens";
import { UserRole } from "../constants/userRoles.ts";

import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import logo from "../assets/logo.png";

const SidebarNav = () => {
  const { user, tenantBranding } = useAuth() || {};

  const role = user?.role ?? null;
  const isLoggedIn = Boolean(user);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useState(false);

  const isTenantUser =
    role === UserRole.TENANT_ADMIN ||
    role === UserRole.TENANT_STAFF;

  const primaryColor =
    isTenantUser &&
    tenantBranding?.primaryColor
      ? tenantBranding.primaryColor
      : colors.scalpelTeal[400];

  const secondaryColor =
    isTenantUser &&
    tenantBranding?.secondaryColor
      ? tenantBranding.secondaryColor
      : colors.houndGold[400];

  const sidebarLogo =
    isTenantUser && tenantBranding?.logoUrl
      ? tenantBranding.logoUrl
      : logo;

  const logoAlt =
    isTenantUser && tenantBranding?.name
      ? tenantBranding.name
      : "Operational Clarity";

  const userName = user
    ? `${user.firstName || ""} ${
        user.lastName || ""
      }`.trim()
    : "Not signed in";

  const formatRoleLabel = (userRole) => {
    switch (userRole) {
      case UserRole.SYSTEM_ADMIN:
        return "System Admin";

      case UserRole.ACCOUNT_MANAGER:
        return "Account Manager";

      case UserRole.TENANT_ADMIN:
        return "Tenant Admin";

      case UserRole.TENANT_STAFF:
        return "Tenant Staff";

      default:
        return "No access role";
    }
  };

  const getDisplayText = (item) => {
    if (
      role === UserRole.TENANT_ADMIN &&
      item.tenantText
    ) {
      return item.tenantText;
    }

    return item.text;
  };

  const hasSectionAccess = (section) => {
    if (section.public) {
      return true;
    }

    if (!isLoggedIn || !role) {
      return false;
    }

    return section.roles?.includes(role);
  };

  const hasItemAccess = (item) => {
    if (item.publicOnly) {
      return !isLoggedIn;
    }

    if (item.public) {
      return true;
    }

    if (!isLoggedIn || !role) {
      return false;
    }

    if (
      !item.roles ||
      item.roles.length === 0
    ) {
      return true;
    }

    return item.roles.includes(role);
  };

  const visibleSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        hasItemAccess
      ),
    }))
    .filter(
      (section) =>
        hasSectionAccess(section) &&
        section.items.length > 0
    );

  const userRoleLabel =
    formatRoleLabel(role);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isCollapsed ? 80 : 250,
        flexShrink: 0,

        "& .MuiDrawer-paper": {
          width: isCollapsed ? 80 : 250,
          boxSizing: "border-box",
          backgroundColor:
            "background.paper",
          color: colors.grey[100],
        },
      }}
    >
      <Box p={2}>
        <Box
          display="flex"
          justifyContent={
            isCollapsed
              ? "center"
              : "space-between"
          }
          alignItems="center"
        >
          <IconButton
            onClick={() =>
              setIsCollapsed(
                (current) => !current
              )
            }
          >
            <MenuOutlinedIcon
              sx={{ color: primaryColor }}
            />
          </IconButton>
        </Box>

        {!isCollapsed && (
          <Box textAlign="center" mt={2}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                width: "100%",
                mt: 2,
              }}
            >
              <Box
                component="img"
                src={sidebarLogo}
                alt={logoAlt}
                sx={{
                  width: 200,
                  height: 200,
                  objectFit: "contain",
                }}
              />
            </Box>

            {user && (
              <>
                <Typography
                  variant="h3"
                  color={primaryColor}
                  mt={1}
                >
                  {userName}
                </Typography>

                <Typography
                  variant="body2"
                  color={secondaryColor}
                >
                  {userRoleLabel}
                </Typography>
              </>
            )}
          </Box>
        )}

        <List>
          {visibleSections.map(
            (section, sectionIndex) => {
              const sectionKey = `${
                section.key || "section"
              }-${sectionIndex}`;

              return (
                <Box key={sectionKey}>
                  {!isCollapsed &&
                    section.title && (
                      <Typography
                        sx={{
                          m: "20px 0 5px 15px",
                        }}
                        variant="h5"
                        color={secondaryColor}
                      >
                        {section.title}
                      </Typography>
                    )}

                  {section.items.map(
                    (item, itemIndex) => {
                      const isActive =
                        location.pathname ===
                        item.path;

                      const displayText =
                        getDisplayText(item);

                      const itemKey = `${sectionKey}-${
                        item.path ||
                        item.text ||
                        "item"
                      }-${itemIndex}`;

                      return (
                        <ListItem
                          disablePadding
                          key={itemKey}
                        >
                          <ListItemButton
                            component={Link}
                            to={item.path}
                            sx={{
                              mb: 1,
                              bgcolor: isActive
                                ? primaryColor
                                : "transparent",

                              "&:hover": {
                                bgcolor: isActive
                                  ? primaryColor
                                  : `${primaryColor}33`,
                              },

                              borderRadius: 1,

                              color: isActive
                                ? "#fff"
                                : colors.grey?.[100],
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: isActive
                                  ? "#fff"
                                  : primaryColor,
                                minWidth: 36,
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>

                            {!isCollapsed && (
                              <ListItemText
                                primary={
                                  displayText
                                }
                              />
                            )}
                          </ListItemButton>
                        </ListItem>
                      );
                    }
                  )}
                </Box>
              );
            }
          )}
        </List>
      </Box>
    </Drawer>
  );
};

export default SidebarNav;